import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, BookOpen, CheckCircle } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useSeriesDetail } from '../../hooks/useSeriesDetail';
import { useSeriesImportStatus } from '../../hooks/useSeriesImportStatus';
import { SeriesSermonList } from './SeriesSermonList';
import { SeriesAssetTabs } from './SeriesAssetTabs';
import { ImportSeriesModal } from './ImportSeriesModal';

export function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasSeriesAccess, loading: subLoading } = useSubscription();
  const { series, sermons, assets, loading, error } = useSeriesDetail(id);
  const importStatus = useSeriesImportStatus(id);
  const [importOpen, setImportOpen] = React.useState(false);

  useEffect(() => {
    if (!subLoading && !hasSeriesAccess) {
      navigate('/dashboard/series-library', { replace: true });
    }
  }, [subLoading, hasSeriesAccess, navigate]);

  if (subLoading || loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate/40" />
      </div>
    );
  }

  if (error || !series) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-sm text-red-500">{error ?? 'Series not found.'}</p>
        <button onClick={() => navigate('/dashboard/series-library')} className="text-sm text-electric hover:underline">
          Back to Library
        </button>
      </div>
    );
  }

  const { canImport, alreadyImported, importsRemaining, resetsAt } = importStatus;
  const resetDateStr = resetsAt ? resetsAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
  const assetSummary = [
    { label: 'Graphics', count: assets.filter(a => a.asset_type === 'graphic').length },
    { label: 'Social', count: assets.filter(a => a.asset_type === 'social').length },
    { label: 'Slides', count: assets.filter(a => a.asset_type === 'slide').length },
    { label: 'Outlines', count: assets.filter(a => a.asset_type === 'outline').length },
  ].filter(a => a.count > 0);

  return (
    <div className="min-h-full">
      {/* Hero — full bleed, no max-width constraint */}
      <div className="relative bg-midnight overflow-hidden">
        {/* Blurred cover as full background */}
        {series.cover_art_url && (
          <div
            className="absolute inset-0 bg-cover bg-center scale-110 opacity-30 blur-2xl"
            style={{ backgroundImage: `url(${series.cover_art_url})` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-midnight/95 via-midnight/70 to-midnight/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-transparent to-transparent" />

        {/* Breadcrumb — full width */}
        <div className="relative px-6 sm:px-10 pt-5">
          <button
            onClick={() => navigate('/dashboard/series-library')}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Library
          </button>
        </div>

        {/* Hero content — full width, two columns */}
        <div className="relative flex flex-col md:flex-row gap-8 md:gap-12 px-6 sm:px-10 pt-6 pb-10 items-center">
          {/* Cover art — takes real space */}
          <div className="w-full md:w-[42%] shrink-0">
            {series.cover_art_url
              ? <img
                  src={series.cover_art_url}
                  alt={series.title}
                  className="w-full rounded-2xl shadow-2xl object-cover"
                  style={{ aspectRatio: '16/9' }}
                />
              : <div
                  className="w-full rounded-2xl bg-white/10 flex items-center justify-center shadow-2xl"
                  style={{ aspectRatio: '16/9' }}
                >
                  <BookOpen className="h-16 w-16 text-white/20" />
                </div>
            }
          </div>

          {/* Meta — right side */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mb-4">
              {series.book_of_bible && (
                <span className="text-xs px-3 py-1 bg-electric/25 border border-electric/40 text-electric rounded-full font-medium">
                  {series.book_of_bible}
                </span>
              )}
              {series.tags.map(tag => (
                <span key={tag} className="text-xs px-3 py-1 bg-white/10 border border-white/15 text-white/70 rounded-full">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">{series.title}</h1>

            <p className="text-sm text-white/50 mb-3">
              {series.week_count} {series.week_count === 1 ? 'sermon' : 'sermons'}
              {series.topic ? ` · ${series.topic}` : ''}
            </p>

            {series.description && (
              <p className="text-base text-white/70 mb-6 leading-relaxed max-w-xl">{series.description}</p>
            )}

              {/* Import CTA */}
              {importStatus.loading ? (
                <div className="flex items-center gap-2 text-sm text-white/50">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
              ) : alreadyImported ? (
                <div className="flex items-center gap-2 text-sm text-emerald-400">
                  <CheckCircle className="h-4 w-4" /> Already in your library
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <button
                    onClick={() => setImportOpen(true)}
                    disabled={!canImport}
                    className="flex items-center gap-2.5 px-6 py-3 bg-electric text-white font-semibold rounded-xl hover:bg-electric/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors w-fit text-sm"
                  >
                    Import Series
                    {canImport && (
                      <span className="text-[11px] bg-white/20 px-2 py-0.5 rounded-full font-medium">
                        {importsRemaining} of 2 remaining
                      </span>
                    )}
                  </button>
                  {!canImport && importsRemaining === 0 && (
                    <p className="text-xs text-white/40">
                      Limit reached{resetDateStr ? ` · resets ${resetDateStr}` : ''}
                    </p>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-10">

        {/* This Series */}
        {sermons.length > 0 && (
          <section>
            <p className="text-[11px] font-bold text-slate dark:text-soft-white/40 uppercase tracking-widest mb-4">
              This Series
            </p>
            <SeriesSermonList sermons={sermons} isImported={alreadyImported} />
          </section>
        )}

        {/* What's Included */}
        {assets.length > 0 && (
          <section>
            <div className="flex items-baseline justify-between mb-4">
              <p className="text-[11px] font-bold text-slate dark:text-soft-white/40 uppercase tracking-widest">
                What's Included
              </p>
              {assetSummary.length > 0 && (
                <p className="text-xs text-slate dark:text-soft-white/40">
                  {assetSummary.map(a => `${a.count} ${a.label}`).join(' · ')}
                </p>
              )}
            </div>
            <SeriesAssetTabs assets={assets} />
          </section>
        )}

        {/* Empty state when no sermons or assets yet */}
        {sermons.length === 0 && assets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center gap-2 border-2 border-dashed border-sand/30 dark:border-slate/20 rounded-xl">
            <BookOpen className="h-8 w-8 text-slate/20" />
            <p className="text-sm text-slate dark:text-soft-white/40">Sermons and resources coming soon.</p>
          </div>
        )}
      </div>

      {importOpen && <ImportSeriesModal series={series} onClose={() => setImportOpen(false)} />}
    </div>
  );
}
